import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getProfileImageUrl } from '@/lib/utils';
import Navbar from './Navbar';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutDashboard, 
  Users, 
  User,
  Bell,
  MessageSquare, 
  Video, 
  Calendar, 
  Settings,
  UsersRound,
  Sparkles,
  Crown,
  Palmtree,
  Map,
  Shield,
  Briefcase,
  CreditCard,
  LogOut,
  ShoppingBag,
  BookOpen,
  BarChart3,
  Heart,
  Radio,
  Bookmark,
  FileSignature,
  Target,
  RefreshCw,
  Star,
  Compass,
  Flame,
  ChevronDown,
  ChevronRight,
  Sprout,
  Trophy,
  Lock,
  Gift,
  Send,
  Mail,
  Link2
} from 'lucide-react';

import BirthdayPopup from '@/components/BirthdayPopup';
import BuildVersionStamp from '@/components/admin/BuildVersionStamp';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';

const API_URL = window.location.origin;

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasTier, hasInnerCircleAccess, hasCoachAccess, hasSDCAccess, hasCostaRicaAccess, canAccessChallenges, daysUntilChallengesUnlock } = useAuth();
  
  // State for collapsible sections
  const [growthToolsOpen, setGrowthToolsOpen] = useState(
    // Auto-open if user is on a growth tools page
    ['/truth-reset', '/sister-spotlights', '/legacy-vision', '/challenges', '/activation-pathway'].some(p => location.pathname.startsWith(p))
  );
  
  const [communityHubOpen, setCommunityHubOpen] = useState(
    // Auto-open if user is on a community page
    ['/communities', '/circles', '/messages', '/directory', '/saved'].some(p => location.pathname.startsWith(p))
  );

  // Tablet/mobile sidebar drawer (below lg the fixed sidebar is hidden)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Build sidebar links based on user's tiers - OPTIMIZED FOR MEMBER IMPORTANCE
  const getSidebarLinks = () => {
    // Admin-specific sidebar (includes Wonder Twin dashboard for Crystal/Dr. Nicole)
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      const adminLinks = [
        { href: '/admin', icon: LayoutDashboard, label: 'Admin Dashboard' },
        { href: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
        { href: '/admin/users', icon: Users, label: 'Users' },
        { href: '/admin/coaches', icon: Briefcase, label: 'Coaches' },
        { href: '/admin/group-sessions', icon: Calendar, label: 'Group Sessions' },
        { href: '/admin/requests', icon: Sparkles, label: 'Requests' },
        { href: '/admin/coach-notes', icon: MessageSquare, label: 'Coach Notes' },
        { href: '/admin/invoices', icon: Briefcase, label: 'Invoices' },
        { href: '/admin/billing', icon: CreditCard, label: 'Billing' },
        { href: '/admin/broadcast', icon: Send, label: 'Broadcast' },
        { href: '/admin/communications', icon: Mail, label: 'Comms Log' },
        { href: '/admin/email-templates', icon: FileSignature, label: 'Email Templates' },
        { href: '/admin/soul-food', icon: BookOpen, label: 'Soul Food' },
        { href: '/admin/content', icon: Radio, label: 'Content' },
        { href: '/admin/communities', icon: UsersRound, label: 'Sister Circles' },
        { href: '/admin/legacy', icon: Crown, label: 'Legacy Circle' },
        { href: '/admin/retreat', icon: Palmtree, label: 'Costa Rica Retreat' },
        { href: '/admin/collective', icon: Sparkles, label: 'Collective Apps' },
        { href: '/admin/gift-applications', icon: Heart, label: 'Gift Applications' },
        { href: '/admin/affiliates', icon: Link2, label: 'Affiliates' },
        { href: '/admin/safety', icon: Shield, label: 'Safety' },
        { href: '/admin/esign', icon: FileSignature, label: 'E-Signatures' },
        { href: '/admin/gifts', icon: Gift, label: 'Gift Tracking' },
        { href: '/videos', icon: Video, label: 'The Vault' },
        { href: '/admin/messages', icon: MessageSquare, label: 'Messages' },
        { href: '/settings', icon: Settings, label: 'Settings' }
      ];
      
      // Add Admin Management for Super Admins
      if (user?.role === 'super_admin') {
        adminLinks.push({ href: '/admin/manage-admins', icon: Crown, label: 'Manage Admins' });
      }
      
      // Add Wonder Twin Dashboard for Crystal & Dr. Nicole
      if (user?.is_wonder_twin) {
        adminLinks.splice(1, 0, { href: '/wonder-twin', icon: Crown, label: 'Wonder Twin' });
      }
      
      // Add Technical Support link
      adminLinks.push({ href: '/admin/support', icon: Shield, label: 'Tech Support' });
      
      return { primary: adminLinks, growthTools: [], communityHub: [], giftLink: null, secondary: [] };
    }

    // Coach-specific sidebar (different from client sidebar)
    if (user?.is_coach) {
      return {
        primary: [
          { href: '/coach', icon: LayoutDashboard, label: 'Dashboard' },
          { href: '/coach/profile', icon: User, label: 'My Profile' },
          { href: '/coach/sessions', icon: Calendar, label: 'Sessions' },
          { href: '/coach/notes', icon: MessageSquare, label: 'Notes' },
          { href: '/coach/invoices', icon: Briefcase, label: 'Invoices' },
          { href: '/coach/agreements', icon: Sparkles, label: 'Agreements' },
          { href: '/coach/messages', icon: MessageSquare, label: 'Messages' },
          { href: '/coach/settings', icon: Settings, label: 'Settings' }
        ],
        growthTools: [],
        communityHub: [],
        giftLink: null,
        secondary: []
      };
    }

    // MEMBER SIDEBAR - Optimized for importance
    // Priority 1: Core Daily Use
    const primary = [
      { href: '/dashboard', icon: LayoutDashboard, label: 'Home' },
    ];

    if (hasTier('legacy_circle')) {
      primary.push({ href: '/legacy', icon: Crown, label: 'Legacy Circle' });
    }

    if (hasInnerCircleAccess()) {
      primary.push(
        { href: '/journal', icon: BookOpen, label: 'Soul Journal' },
        { href: '/videos', icon: Video, label: 'The Vault' },
        { href: '/my-journey', icon: Trophy, label: 'My Journey' }
      );
    }

    // Priority 2: Growth Tools (Collapsible section)
    const growthTools = [];
    if (hasInnerCircleAccess()) {
      const challengesLocked = !canAccessChallenges();
      const daysRemaining = daysUntilChallengesUnlock();
      
      growthTools.push(
        { href: '/activation-pathway', icon: Target, label: '30-Day Activation' },
        { href: '/truth-reset', icon: RefreshCw, label: 'Truth Reset' },
        { href: '/legacy-vision', icon: Compass, label: 'YUMMY Declaration' },
        { 
          href: challengesLocked ? null : '/challenges', 
          icon: challengesLocked ? Lock : Flame, 
          label: '6-Week Challenges',
          locked: challengesLocked,
          lockedDays: daysRemaining
        },
        { href: '/sister-spotlights', icon: Star, label: 'Sister Spotlights' },
        { href: '/library', icon: BookOpen, label: 'Library' }
      );
    }

    // Priority 3: Community Hub (Collapsible section)
    const communityHub = [];
    if (hasInnerCircleAccess()) {
      communityHub.push(
        { href: '/communities', icon: UsersRound, label: 'Sister Circles' },
        { href: '/messages', icon: MessageSquare, label: 'Messages' },
        { href: '/directory', icon: Users, label: 'Member Directory' },
        { href: '/saved', icon: Bookmark, label: 'Saved Posts' }
      );
    }

    // Gift Membership - Standalone highlight
    const giftLink = hasInnerCircleAccess() 
      ? { href: '/gift-membership', icon: Heart, label: 'Gift a Membership', highlight: true }
      : null;

    // Secondary: Premium Features & Settings
    const secondary = [];
    
    // Inner Edge features
    if (hasTier('inner_edge')) {
      secondary.push(
        { href: '/inner-edge', icon: Sparkles, label: 'Inner Edge Hub' },
        { href: '/diagnostic', icon: Sparkles, label: 'Diagnostic' }
      );
    }

    // Costa Rica Retreat - removed standalone Retreat Hub, content moved to Circle page
    // if (hasCostaRicaAccess()) {
    //   secondary.push({ href: '/retreat', icon: Palmtree, label: 'Retreat Hub' });
    // }

    // Always show these at bottom
    secondary.push(
      { href: 'https://sisterdiamondsmarketplace.com/', icon: ShoppingBag, label: 'YUMMY Shop', external: true },
      { href: '/community-guidelines', icon: Shield, label: 'Guidelines' },
      { href: '/settings', icon: Settings, label: 'Settings' }
    );

    return { primary, growthTools, communityHub, giftLink, secondary };
  };

  const { primary: primaryLinks, growthTools: growthToolsLinks, communityHub: communityHubLinks, giftLink, secondary: secondaryLinks } = getSidebarLinks();

  // Helper function to check if link is active
  const isLinkActive = (link) => {
    if (link.href === '/admin') return location.pathname === '/admin';
    if (link.href === '/coach') return location.pathname === '/coach';
    if (link.href === '/dashboard') return location.pathname === '/dashboard';
    if (link.external) return false;
    return location.pathname.startsWith(link.href);
  };

  // Render a single nav link
  const renderNavLink = (link) => {
    const isActive = isLinkActive(link);
    
    if (link.external) {
      return (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-gray-600 hover:bg-sand hover:text-forest"
          data-testid={`sidebar-${link.label.toLowerCase().replace(/ /g, '-')}`}
        >
          <link.icon className="h-5 w-5" />
          <span className="font-medium text-sm">{link.label}</span>
        </a>
      );
    }

    return (
      <Link
        key={link.href}
        to={link.href}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
          isActive 
            ? 'bg-forest text-white' 
            : link.highlight
              ? 'text-pink-600 hover:bg-pink-50 hover:text-pink-700 border border-pink-200'
              : 'text-gray-600 hover:bg-sand hover:text-forest'
        }`}
        data-testid={`sidebar-${link.label.toLowerCase().replace(/ /g, '-')}`}
      >
        <link.icon className={`h-5 w-5 ${link.highlight && !isActive ? 'text-pink-500' : ''}`} />
        <span className="font-medium text-sm">{link.label}</span>
      </Link>
    );
  };

  // Check if any growth tool is active
  const isGrowthToolActive = growthToolsLinks.some(link => isLinkActive(link));

  const sidebarContent = (
    <>
      <div className="flex-1 py-4 px-3 overflow-y-auto">
            <nav className="space-y-1">
              {/* Primary Links - Most Important */}
              {primaryLinks.map(renderNavLink)}
              
              {/* Growth Tools Section - Collapsible for members */}
              {growthToolsLinks.length > 0 && (
                <div className="pt-4">
                  <button
                    onClick={() => setGrowthToolsOpen(!growthToolsOpen)}
                    className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg transition-colors ${
                      isGrowthToolActive 
                        ? 'bg-terracotta/10 text-terracotta' 
                        : 'text-gray-600 hover:bg-sand hover:text-forest'
                    }`}
                    data-testid="sidebar-growth-tools-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <Sprout className="h-5 w-5" />
                      <span className="font-medium text-sm">Growth Tools</span>
                    </div>
                    {growthToolsOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {growthToolsOpen && (
                    <div className="mt-1 ml-4 space-y-0.5 border-l-2 border-terracotta/20 pl-2">
                      {growthToolsLinks.map(link => {
                        const isActive = !link.locked && isLinkActive(link);
                        
                        // Handle locked items
                        if (link.locked) {
                          return (
                            <div
                              key={link.label}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-400 cursor-not-allowed"
                              data-testid={`sidebar-${link.label.toLowerCase().replace(/ /g, '-')}`}
                              title={`Unlocks in ${link.lockedDays} days`}
                            >
                              <link.icon className="h-4 w-4" />
                              <span className="font-medium flex-1">{link.label}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-gray-50 text-gray-400 border-gray-200">
                                {link.lockedDays}d
                              </Badge>
                            </div>
                          );
                        }
                        
                        return (
                          <Link
                            key={link.href}
                            to={link.href}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                              isActive 
                                ? 'bg-terracotta text-white' 
                                : 'text-gray-600 hover:bg-terracotta/10 hover:text-terracotta'
                            }`}
                            data-testid={`sidebar-${link.label.toLowerCase().replace(/ /g, '-')}`}
                          >
                            <link.icon className="h-4 w-4" />
                            <span className="font-medium">{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* Community Hub Section - Collapsible for members */}
              {communityHubLinks && communityHubLinks.length > 0 && (
                <div className="pt-3">
                  <button
                    onClick={() => setCommunityHubOpen(!communityHubOpen)}
                    className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg transition-colors ${
                      communityHubLinks.some(link => isLinkActive(link))
                        ? 'bg-forest/10 text-forest' 
                        : 'text-gray-600 hover:bg-sand hover:text-forest'
                    }`}
                    data-testid="sidebar-community-hub-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <UsersRound className="h-5 w-5" />
                      <span className="font-medium text-sm">Community Hub</span>
                    </div>
                    {communityHubOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {communityHubOpen && (
                    <div className="mt-1 ml-4 space-y-0.5 border-l-2 border-forest/20 pl-2">
                      {communityHubLinks.map(link => {
                        const isActive = isLinkActive(link);
                        return (
                          <Link
                            key={link.href}
                            to={link.href}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                              isActive 
                                ? 'bg-forest text-white' 
                                : 'text-gray-600 hover:bg-forest/10 hover:text-forest'
                            }`}
                            data-testid={`sidebar-${link.label.toLowerCase().replace(/ /g, '-')}`}
                          >
                            <link.icon className="h-4 w-4" />
                            <span className="font-medium">{link.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* Gift Membership - Standalone Highlight */}
              {giftLink && (
                <div className="pt-3">
                  <Link
                    to={giftLink.href}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                      isLinkActive(giftLink)
                        ? 'bg-pink-600 text-white'
                        : 'text-pink-600 hover:bg-pink-50 hover:text-pink-700 border border-pink-200 bg-pink-50/50'
                    }`}
                    data-testid="sidebar-gift-a-membership"
                  >
                    <giftLink.icon className={`h-5 w-5 ${!isLinkActive(giftLink) ? 'text-pink-500' : ''}`} />
                    <span className="font-medium text-sm">{giftLink.label}</span>
                  </Link>
                </div>
              )}
              
              {/* More Section - Settings & External Links */}
              {secondaryLinks.length > 0 && (
                <div className="pt-4 mt-4 border-t border-sand/50">
                  <p className="px-4 py-1 text-xs text-gray-400 uppercase tracking-wide">More</p>
                  {secondaryLinks.map(renderNavLink)}
                </div>
              )}
            </nav>
          </div>

          {/* User Info & Logout */}
          {user && (
            <div className="p-3 border-t border-sand">
              <div className="flex items-center gap-3 mb-2 px-1">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={getProfileImageUrl(user.profile_image)} alt={user.full_name} />
                  <AvatarFallback className="bg-forest text-white text-sm">
                    {user.full_name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{user.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="sidebar-logout"
              >
                <LogOut className="h-4 w-4" />
                <span>Log Out</span>
              </button>
              <BuildVersionStamp />
            </div>
          )}
    </>
  );

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      {/* Birthday Popup - shows on birthday or birthday month */}
      <BirthdayPopup />
      
      <div className="flex pt-16">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex w-64 flex-col fixed left-0 top-16 bottom-0 bg-white border-r border-sand">
          {sidebarContent}
        </aside>

        {/* Tablet/Mobile Sidebar Drawer */}
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-72 p-0 pt-6 flex flex-col lg:hidden">
            {sidebarContent}
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64 min-h-[calc(100vh-4rem)]">
          {/* Menu button for tablets/small screens (sidebar hidden below lg) */}
          <div className="lg:hidden px-4 pt-4 -mb-2">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-white text-sm font-medium shadow-md hover:bg-forest/90 transition-colors"
              data-testid="mobile-sidebar-toggle"
              aria-label="Open navigation menu"
            >
              <Menu className="h-4 w-4" />
              Menu
            </button>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
